import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  X,
  CheckCircle2,
  Loader2,
  ThumbsUp,
  ThumbsDown,
  CalendarCheck,
  StickyNote,
  ChevronDown,
  ExternalLink,
  Pencil,
  Clock,
  Lock,
} from "lucide-react";
import StructuredForm from "./StructuredForm";
import { useToast } from "./Toast";
import { useAuth } from "../context/AuthContext";
import { requestSkoolInvite } from "../lib/queries";

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("es-ES", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function NotesInput({ notes, setNotes }) {
  const [open, setOpen] = useState(!!notes);

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-brand transition-colors mt-4"
      >
        <StickyNote size={13} />
        Añadir notas
        <ChevronDown size={12} />
      </button>
    );
  }

  return (
    <div className="mt-4 space-y-1.5">
      <label className="text-xs font-medium text-gray-400">
        Notas (opcional)
      </label>
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Escribe tus notas aquí..."
        rows={2}
        className="w-full text-sm border border-gray-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand resize-none"
      />
    </div>
  );
}

function parseResponse(response) {
  if (!response) return {};
  if (typeof response === "object") return response;
  try {
    return JSON.parse(response);
  } catch {
    return {};
  }
}

// Solo se muestra el `response` en crudo si es una string legible (yesno/text).
// Oculta el JSON de un formulario structured "dormido" (empieza por { o [) o
// cualquier valor que no sea texto plano.
function isReadableTextResponse(response) {
  if (typeof response !== "string") return false;
  const trimmed = response.trim();
  if (!trimmed) return false;
  return trimmed[0] !== "{" && trimmed[0] !== "[";
}

export default function ActivityModal({ task, onComplete, onClose }) {
  const [text, setText] = useState(task.response ?? "");
  const [notes, setNotes] = useState(task.notes ?? "");
  const [structuredValues, setStructuredValues] = useState(() =>
    parseResponse(task.response)
  );
  const [saving, setSaving] = useState(false);
  const [visible, setVisible] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const { showError } = useToast();
  const { user } = useAuth();

  const readOnly = task.completed && !isEditing;

  useEffect(() => {
    document.body.style.overflow = "hidden";
    requestAnimationFrame(() => setVisible(true));
    return () => { document.body.style.overflow = ""; };
  }, []);

  const close = () => {
    setVisible(false);
    setTimeout(onClose, 250);
  };

  const submit = async (response) => {
    setSaving(true);
    try {
      const payload = {
        completed: true,
        response: response ?? null,
        notes: notes.trim() || null,
      };

      if (!isEditing) {
        payload.completed_at = new Date().toISOString();
      }

      await onComplete(task.id, payload);
      close();
    } catch (err) {
      showError("No se pudo guardar la actividad. Inténtalo de nuevo.");
      setSaving(false);
    }
  };

  const submitStructured = async () => {
    setSaving(true);
    try {
      const payload = {
        completed: true,
        response: JSON.stringify(structuredValues),
        notes: notes.trim() || null,
      };

      if (!isEditing) {
        payload.completed_at = new Date().toISOString();
      }

      await onComplete(task.id, payload);
      close();
    } catch (err) {
      showError("No se pudo guardar la actividad. Inténtalo de nuevo.");
      setSaving(false);
    }
  };

  const isText = task.input_type === "text";
  const isYesNo = task.input_type === "yesno";
  const isStructured = task.input_type === "structured";
  const isSkoolInvite = task.input_type === "skool_invite";

  // Solo se considera "respuesta mostrable" si es texto legible (yesno/text).
  // El JSON de un structured convertido a checkbox NO cuenta como respuesta.
  const hasReadableResponse =
    !isStructured && isReadableTextResponse(task.response);

  const submitSkoolInvite = async () => {
    setSaving(true);
    try {
      await requestSkoolInvite();
      await onComplete(task.id, {
        completed: true,
        completed_at: new Date().toISOString(),
        response: user.email,
      });
      close();
    } catch (err) {
      showError("No se pudo enviar la invitación. Inténtalo de nuevo.");
      setSaving(false);
      setShowConfirm(false);
    }
  };

  const hasStructuredValues =
    Object.values(structuredValues).some((v) => v && String(v).trim());

  const structuredReadOnlyContent = (
    <>
      <div className="flex items-start justify-between gap-3 mb-2">
        <h3 className="text-base font-semibold text-gray-900 leading-snug">
          {task.name}
        </h3>
        <button
          onClick={() => setIsEditing(true)}
          className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-brand bg-brand-light hover:bg-brand-light/80 rounded-lg transition-colors"
        >
          <Pencil size={12} />
          Editar
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-6">
        <div className="flex items-center gap-1.5">
          <CalendarCheck size={13} className="text-emerald-500" />
          <span className="text-xs text-gray-500">
            Completada el {formatDate(task.completed_at)}
          </span>
        </div>
        {task.updated_at && task.completed_at && new Date(task.updated_at) > new Date(task.completed_at) && (
          <div className="flex items-center gap-1.5">
            <Clock size={13} className="text-blue-500" />
            <span className="text-xs text-gray-500">
              Editada el {formatDate(task.updated_at)}
            </span>
          </div>
        )}
      </div>

      <StructuredForm
        config={task.config}
        values={parseResponse(task.response)}
        readOnly={true}
        userId={user?.id}
      />

      {task.notes && (
        <div className="space-y-1.5 mt-4">
          <label className="text-xs font-medium text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
            <StickyNote size={12} />
            Tus notas
          </label>
          <div className="bg-amber-50 rounded-xl px-4 py-3">
            <p className="text-sm text-gray-700 whitespace-pre-wrap">
              {task.notes}
            </p>
          </div>
        </div>
      )}
    </>
  );

  const structuredEditContent = (
    <>
      <div className="flex items-start justify-between gap-3 mb-4">
        <h3 className="text-base font-semibold text-gray-900 leading-snug">
          {task.name}
        </h3>
        {isEditing && (
          <button
            onClick={() => setIsEditing(false)}
            className="shrink-0 text-xs text-gray-400 hover:text-gray-600 transition-colors"
          >
            Cancelar
          </button>
        )}
      </div>

      <StructuredForm
        config={task.config}
        values={structuredValues}
        onChange={setStructuredValues}
        readOnly={false}
        userId={user?.id}
      />

      <NotesInput notes={notes} setNotes={setNotes} />

      <button
        onClick={submitStructured}
        disabled={saving || !hasStructuredValues}
        className="w-full mt-5 px-4 py-3 text-sm font-medium text-white bg-brand hover:bg-brand-dark disabled:bg-gray-200 disabled:text-gray-400 rounded-xl transition-colors flex items-center justify-center gap-2"
      >
        {saving ? (
          <Loader2 size={15} className="animate-spin" />
        ) : (
          <>
            <CheckCircle2 size={15} />
            {isEditing ? "Guardar cambios" : "Completar actividad"}
          </>
        )}
      </button>
    </>
  );

  const readOnlyContent = (
    <>
      <div className="flex items-start justify-between gap-3 mb-2">
        <h3 className="text-base font-semibold text-gray-900 leading-snug">
          {task.name}
        </h3>
        {(isText || isYesNo) && (
          <button
            onClick={() => setIsEditing(true)}
            className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-brand bg-brand-light hover:bg-brand-light/80 rounded-lg transition-colors"
          >
            <Pencil size={12} />
            Editar
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-6">
        <div className="flex items-center gap-1.5">
          <CalendarCheck size={13} className="text-emerald-500" />
          <span className="text-xs text-gray-500">
            Completada el {formatDate(task.completed_at)}
          </span>
        </div>
        {task.updated_at && task.completed_at && new Date(task.updated_at) > new Date(task.completed_at) && (
          <div className="flex items-center gap-1.5">
            <Clock size={13} className="text-blue-500" />
            <span className="text-xs text-gray-500">
              Editada el {formatDate(task.updated_at)}
            </span>
          </div>
        )}
      </div>

      {!isText && !isYesNo && !isStructured && !isSkoolInvite && task.description && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl">
          <p className="text-sm text-amber-700 whitespace-pre-wrap">
            {task.description}
          </p>
        </div>
      )}

      {hasReadableResponse && (
        <div className="space-y-1.5 mb-4">
          <label className="text-xs font-medium text-gray-400 uppercase tracking-wider">
            Tu respuesta
          </label>
          <div className="bg-gray-50 rounded-xl px-4 py-3">
            <p className="text-sm text-gray-700">{task.response}</p>
          </div>
        </div>
      )}

      {task.notes && (
        <div className="space-y-1.5 mb-4">
          <label className="text-xs font-medium text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
            <StickyNote size={12} />
            Tus notas
          </label>
          <div className="bg-amber-50 rounded-xl px-4 py-3">
            <p className="text-sm text-gray-700 whitespace-pre-wrap">
              {task.notes}
            </p>
          </div>
        </div>
      )}

      {task.link && (
        <a
          href={task.link}
          target="_blank"
          rel="noopener noreferrer"
          className="mb-4 w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium text-brand bg-brand-light hover:bg-brand-light/80 rounded-xl transition-colors"
        >
          <ExternalLink size={15} />
          Abrir enlace
        </a>
      )}

      {!hasReadableResponse && !task.notes && !task.link && (
        <div className="bg-emerald-50 rounded-xl px-4 py-3 flex items-center gap-2">
          <CheckCircle2 size={16} className="text-emerald-500" />
          <p className="text-sm text-emerald-700 font-medium">
            Actividad completada
          </p>
        </div>
      )}
    </>
  );

  const editContent = (
    <>
      <div className="flex items-start justify-between gap-3 mb-6">
        <h3 className="text-base font-semibold text-gray-900 leading-snug">
          {task.name}
        </h3>
        {isEditing && (
          <button
            onClick={() => setIsEditing(false)}
            className="shrink-0 text-xs text-gray-400 hover:text-gray-600 transition-colors"
          >
            Cancelar
          </button>
        )}
      </div>

      {isYesNo && (
        <div>
          {task.description && (
            <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-xl">
              <p className="text-sm text-amber-700 whitespace-pre-wrap">
                {task.description}
              </p>
            </div>
          )}
          <div className="space-y-3">
            <button
              onClick={() => submit(task.config?.yesLabel || "Sí")}
              disabled={saving}
              className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border-2 border-emerald-200 bg-emerald-50 hover:bg-emerald-100 transition-colors text-left"
            >
              <ThumbsUp size={18} className="text-emerald-600 shrink-0" />
              <span className="text-sm font-semibold text-emerald-700">
                {task.config?.yesLabel || "Sí"}
              </span>
            </button>
            <button
              onClick={() => submit(task.config?.noLabel || "No")}
              disabled={saving}
              className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border-2 border-orange-200 bg-orange-50 hover:bg-orange-100 transition-colors text-left"
            >
              <ThumbsDown size={18} className="text-orange-600 shrink-0" />
              <span className="text-sm font-semibold text-orange-700">
                {task.config?.noLabel || "No"}
              </span>
            </button>
          </div>
          <NotesInput notes={notes} setNotes={setNotes} />
        </div>
      )}

      {isText && (
        <div className="space-y-3">
          <label className="text-xs font-medium text-gray-500">
            Especifica día y franja horaria
          </label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Ej: Lunes y miércoles de 10:00 a 10:30"
            rows={3}
            className="w-full text-sm border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand resize-none"
            autoFocus
          />
          <NotesInput notes={notes} setNotes={setNotes} />
          <button
            onClick={() => submit(text.trim())}
            disabled={saving || !text.trim()}
            className="w-full px-4 py-3 text-sm font-medium text-white bg-brand hover:bg-brand-dark disabled:bg-gray-200 disabled:text-gray-400 rounded-xl transition-colors flex items-center justify-center gap-2"
          >
            {saving ? (
              <Loader2 size={15} className="animate-spin" />
            ) : (
              <>
                <CheckCircle2 size={15} />
                {isEditing ? "Guardar cambios" : "Enviar respuesta"}
              </>
            )}
          </button>
        </div>
      )}

      {!isYesNo && !isText && !isStructured && task.admin_only && (
        <div>
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-2">
            <Lock size={16} className="text-amber-600 shrink-0 mt-0.5" />
            <p className="text-sm text-amber-700">
              Un responsable de ETC validará esta tarea tras tu llamada.
            </p>
          </div>
          {task.link && (
            <a
              href={task.link}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium text-brand bg-brand-light hover:bg-brand-light/80 rounded-xl transition-colors"
            >
              <ExternalLink size={15} />
              Abrir enlace
            </a>
          )}
        </div>
      )}

      {!isYesNo && !isText && !isStructured && !task.admin_only && (
        <div>
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-sm text-gray-600">
              Marca esta actividad como completada cuando la hayas realizado.
            </p>
          </div>
          {task.description && (
            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-xl">
              <p className="text-sm text-amber-700">{task.description}</p>
            </div>
          )}
          {task.link && (
            <a
              href={task.link}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium text-brand bg-brand-light hover:bg-brand-light/80 rounded-xl transition-colors"
            >
              <ExternalLink size={15} />
              Abrir enlace
            </a>
          )}
          <NotesInput notes={notes} setNotes={setNotes} />
          <button
            onClick={() => submit(null)}
            disabled={saving}
            className="w-full mt-4 px-4 py-3 text-sm font-medium text-white bg-brand hover:bg-brand-dark disabled:bg-gray-200 disabled:text-gray-400 rounded-xl transition-colors flex items-center justify-center gap-2"
          >
            {saving ? (
              <Loader2 size={15} className="animate-spin" />
            ) : (
              <>
                <CheckCircle2 size={15} />
                Completar
              </>
            )}
          </button>
        </div>
      )}
    </>
  );

  const skoolInviteContent = task.completed ? (
    <>
      <h3 className="text-base font-semibold text-gray-900 mb-4">
        {task.name}
      </h3>
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 space-y-2">
        <div className="flex items-center gap-2">
          <CheckCircle2 size={16} className="text-emerald-500" />
          <p className="text-sm font-medium text-emerald-700">
            Acceso solicitado
          </p>
        </div>
        <p className="text-sm text-emerald-600">
          Recibirás un email de Skool con un botón <strong>"Join now"</strong>.
          Al pulsarlo tendrás acceso a la academia en la plataforma.
        </p>
      </div>
    </>
  ) : (
    <>
      <h3 className="text-base font-semibold text-gray-900 mb-4">
        {task.name}
      </h3>

      {!showConfirm ? (
        <div className="space-y-4">
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-sm text-gray-600">
              Al solicitar acceso, recibirás un email de Skool con un botón
              <strong> "Join now"</strong> para unirte a la academia.
            </p>
          </div>
          <p className="text-xs text-gray-400">
            Se enviará la invitación a: <span className="font-medium text-gray-600">{user.email}</span>
          </p>
          <button
            onClick={() => setShowConfirm(true)}
            className="w-full px-4 py-3 text-sm font-medium text-white bg-brand hover:bg-brand-dark rounded-xl transition-colors flex items-center justify-center gap-2"
          >
            Solicitar acceso a Skool
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <p className="text-sm text-amber-800">
              Se va a invitar a Skool al email:
            </p>
            <p className="text-sm font-semibold text-amber-900 mt-1">
              {user.email}
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowConfirm(false)}
              disabled={saving}
              className="flex-1 px-4 py-3 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={submitSkoolInvite}
              disabled={saving}
              className="flex-1 px-4 py-3 text-sm font-medium text-white bg-brand hover:bg-brand-dark disabled:bg-gray-200 disabled:text-gray-400 rounded-xl transition-colors flex items-center justify-center gap-2"
            >
              {saving ? (
                <Loader2 size={15} className="animate-spin" />
              ) : (
                "Confirmar"
              )}
            </button>
          </div>
        </div>
      )}
    </>
  );

  const getContent = () => {
    if (isSkoolInvite) return skoolInviteContent;
    if (isStructured) {
      return readOnly ? structuredReadOnlyContent : structuredEditContent;
    }
    return readOnly ? readOnlyContent : editContent;
  };

  const panel = (
    <div className="fixed inset-0 z-[99999]">
      <div
        className={`fixed inset-0 bg-black/40 transition-opacity duration-250 ${
          visible ? "opacity-100" : "opacity-0"
        }`}
        onClick={close}
      />

      {/* Desktop: right drawer */}
      <div
        className={`
          hidden md:flex fixed top-0 right-0 h-full w-full max-w-lg
          flex-col bg-white shadow-2xl
          transition-transform duration-250 ease-out
          ${visible ? "translate-x-0" : "translate-x-full"}
        `}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <span className="text-xs font-semibold text-brand uppercase tracking-wider">
            {readOnly ? "Detalle" : "Actividad"}
          </span>
          <button
            onClick={close}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X size={18} className="text-gray-400" />
          </button>
        </div>

        <div className="flex-1 px-6 py-6 overflow-y-auto">
          {getContent()}
        </div>

        <div className="px-6 py-4 border-t border-gray-100">
          <button
            onClick={close}
            className="w-full px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
          >
            Cerrar
          </button>
        </div>
      </div>

      {/* Mobile: bottom sheet - full screen for structured */}
      <div
        className={`
          md:hidden fixed ${isStructured ? "inset-0" : "bottom-0 left-0 right-0 rounded-t-2xl"}
          bg-white shadow-2xl
          transition-transform duration-250 ease-out
          ${visible ? "translate-y-0" : "translate-y-full"}
          flex flex-col
        `}
      >
        {isStructured ? (
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <span className="text-xs font-semibold text-brand uppercase tracking-wider">
              {readOnly ? "Detalle" : "Actividad"}
            </span>
            <button
              onClick={close}
              className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <X size={18} className="text-gray-400" />
            </button>
          </div>
        ) : (
          <div className="flex justify-center pt-3 pb-1">
            <div className="w-10 h-1 bg-gray-300 rounded-full" />
          </div>
        )}
        <div className={`px-5 py-4 ${isStructured ? "flex-1" : "max-h-[80vh]"} overflow-y-auto`}>
          {getContent()}
          {!isStructured && (
            <button
              onClick={close}
              className="w-full mt-4 px-4 py-3 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
            >
              Cerrar
            </button>
          )}
        </div>
        {isStructured && (
          <div className="px-5 py-4 border-t border-gray-100">
            <button
              onClick={close}
              className="w-full px-4 py-3 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
            >
              Cerrar
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(panel, document.body);
}
